import {
  Prop,
  Type,
  StringProp,
  SelectProp,
  NumberProp,
  BooleanProp,
  DateProp,
  SelectType,
} from './types'

export const cast = (
  literalOrProp: boolean | string | number | Prop,
  outputType: Type,
) => {
  switch (outputType.type) {
    case 'STRING':
      return castString(literalOrProp)
    case 'SELECT':
      return castSelect(literalOrProp, outputType)
    case 'NUMBER':
      return castNumber(literalOrProp)
    case 'BOOLEAN':
      return castBoolean(literalOrProp)
    case 'DATE':
      return castDate(literalOrProp)
    default:
      throw new Error(`Unknown type ${JSON.stringify(outputType)}`)
  }
}
const castString = (
  literalOrProp: boolean | string | number | Prop,
): StringProp => {
  const stringified = JSON.stringify(literalOrProp)
  const isProp = typeof literalOrProp === 'object' && 'type' in literalOrProp
  const isSelectProp = isProp && literalOrProp?.type === 'SELECT'
  const isStringProp = isProp && literalOrProp?.type === 'STRING'
  const isDateProp = isProp && literalOrProp?.type === 'DATE'
  if (isSelectProp) {
    const l = literalOrProp as SelectProp
    return {
      type: 'STRING',
      value: l.value,
    }
  }

  if (isStringProp) {
    return literalOrProp
  }

  if (isDateProp) {
    const l = literalOrProp as DateProp
    return {
      type: 'STRING',
      value: l.value,
    }
  }

  if (typeof literalOrProp === 'string') {
    return { type: 'STRING', value: literalOrProp }
  }

  throw new Error(`${stringified} cannot be cast to STRING`)
}
const castNumber = (
  literalOrProp: boolean | string | number | Prop,
): NumberProp => {
  const stringified = JSON.stringify(literalOrProp)
  const isProp = typeof literalOrProp === 'object' && 'type' in literalOrProp
  const isNumberProp = isProp && literalOrProp?.type === 'NUMBER'
  if (typeof literalOrProp === 'number') {
    return { type: 'NUMBER', value: literalOrProp }
  }

  if (isNumberProp) {
    return literalOrProp
  }

  throw new Error(`${stringified} cannot be cast to NUMBER`)
}
const castBoolean = (
  literalOrProp: boolean | string | number | Prop,
): BooleanProp => {
  const stringified = JSON.stringify(literalOrProp)
  const isProp = typeof literalOrProp === 'object' && 'type' in literalOrProp
  const isBooleanProp = isProp && literalOrProp?.type === 'BOOLEAN'
  if (typeof literalOrProp === 'boolean') {
    return { type: 'BOOLEAN', value: literalOrProp }
  }

  if (isBooleanProp) {
    return literalOrProp
  }

  throw new Error(`${stringified} cannot be cast to BOOLEAN`)
}
const castDate = (
  literalOrProp: boolean | string | number | Prop,
): DateProp => {
  const stringified = JSON.stringify(literalOrProp)
  const isProp = typeof literalOrProp === 'object' && 'type' in literalOrProp
  const isDateProp = isProp && literalOrProp?.type === 'DATE'
  if (typeof literalOrProp === 'string') {
    const dateRegex = /^\d{4}-([0]\d|1[0-2])-([0-2]\d|3[01])$/
    if (!dateRegex.test(literalOrProp)) {
      throw new Error(`${stringified} cannot be case to DATE`)
    }
    return {
      type: 'DATE',
      value: literalOrProp,
    }
  }

  if (isDateProp) {
    return literalOrProp
  }

  throw new Error(`${stringified} cannot be cast to DATE`)
}
const castSelect = (
  literalOrProp: boolean | string | number | Prop,
  outputType: SelectType,
): SelectProp => {
  const stringified = JSON.stringify(literalOrProp)
  const isProp = typeof literalOrProp === 'object' && 'type' in literalOrProp
  const isStringProp = isProp && literalOrProp?.type === 'STRING'
  const isSelectProp = isProp && literalOrProp?.type === 'SELECT'
  if (typeof literalOrProp === 'string') {
    if (!outputType.options.includes(literalOrProp)) {
      throw new Error(
        `${stringified} cannot be cast to SELECT, must be one of ${outputType.options}`,
      )
    }
    return {
      type: 'SELECT',
      value: literalOrProp,
      options: outputType.options,
    }
  }

  if (isStringProp) {
    const l = literalOrProp as StringProp
    if (!outputType.options.includes(l.value)) {
      throw new Error(
        `${stringified} cannot be cast to SELECT, must be one of ${outputType.options}`,
      )
    }
    return {
      type: 'SELECT',
      value: l.value,
      options: outputType.options,
    }
  }

  if (isSelectProp) {
    const l = literalOrProp as SelectProp
    if (!selectOptionsMatch(l, outputType)) {
      throw new Error(`Options don't match between ${l} and ${outputType}`)
    }
    return literalOrProp
  }

  throw new Error(`${stringified} cannot be cast to SELECT`)
}
const selectOptionsMatch = (left: SelectType, right: SelectType) => {
  for (const option of left.options) {
    if (!right.options.includes(option)) {
      return false
    }
  }

  for (const option of right.options) {
    if (!left.options.includes(option)) {
      return false
    }
  }

  return true
}
